/**
 * apps/email/src/main.tsx
 * -----------------------
 * Bootstraps the standalone Email Hub at email.quotech.ai. The actual
 * email feature lives in @quotech/email-hub (extracted from SAM); this
 * file wires the providers SAM relied on (router, query, version watch)
 * around the App shell.
 *
 * Notes:
 *   - No QueryClientProvider was needed in apps/sam at this level
 *     because SAM bootstraps it inside its app shell. The email-hub
 *     package's hooks (useEmails, useEmailStats, etc.) use TanStack
 *     Query, so we provide a QueryClient here.
 */

import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { installChunkErrorHandler, installClientErrorReporter } from '@quotech/ui'
import {
  getAuthHeadersForDiagnostics,
  getOrganisationIdForDiagnostics,
  installReloadInstrumentation,
  makeIdentityScopedQueryKeyHashFn,
} from '@quotech/auth'
import App from './App'
import './index.css'

installChunkErrorHandler()

// Trace + persist every page refresh on the standalone Email Hub. Wraps
// window.location.{reload,replace,assign}: each reload is console-logged,
// pushed onto a sessionStorage trail, and POSTed to
// /api/diagnostics/client-error/ as a `reload_loop` row (so support can see
// WHEN + WHY a tab refreshed), and a runaway loop self-limits at the 4th
// bounce in 10s. The master SPA has had this inline since 2026-05-21; the
// standalone email app shipped with NO reload watchdog at all, so a
// "refresh glitch" here previously left zero server trail.
installReloadInstrumentation({
  tenantTag: 'email',
  buildSha: import.meta.env.VITE_BUILD_SHA,
  // org hint so a reload row attributes to an org (the bare reload POST
  // has no Authorization header, so the BE can't otherwise attribute it).
  getOrganisationId: getOrganisationIdForDiagnostics,
})

// Capture runtime errors + failed fetches to /api/diagnostics/client-error/
// so support agents can root-cause opaque crashes without DevTools.
// See packages/ui/src/diagnostics/clientErrorReporter.ts.
installClientErrorReporter({
  buildSha: import.meta.env.VITE_BUILD_SHA,
  getAuthHeaders: getAuthHeadersForDiagnostics,
  getOrganisationId: getOrganisationIdForDiagnostics,
})

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      // Identity-scoped queryKey hashing — partitions the cache per
      // [userId, orgId] for defence-in-depth against cross-tab
      // identity drift (BUG-2026-05-21-CROSS-TAB-IDENTITY-DRIFT).
      queryKeyHashFn: makeIdentityScopedQueryKeyHashFn(),
    },
  },
})

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      {/* unstable_useTransitions={false} — see apps/newmark/src/main.tsx. (qf!3010, May 2026) */}
      <BrowserRouter unstable_useTransitions={false}>
        <App />
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
)
